import React, { useState, useEffect } from 'react';
import comarcaEmblem from '../assets/images/logo/comarca_emblem.png';
import './Loading.css';
import { useAdminDesign } from './admin/AdminDesignScope';
import AdminCommunityLoading from './admin/AdminCommunityLoading';

interface LoadingProps {
    text?: string;
    fullScreen?: boolean;
    compact?: boolean;
}

const Loading: React.FC<LoadingProps> = ({ text = 'Cargando...', fullScreen = false, compact = false }) => {
    const isAdmin = useAdminDesign();
    const [isOnline, setIsOnline] = useState(true);

    useEffect(() => {
        const handleOnlineStatus = () => setIsOnline(navigator.onLine);

        setIsOnline(navigator.onLine);
        window.addEventListener('online', handleOnlineStatus);
        window.addEventListener('offline', handleOnlineStatus);

        return () => {
            window.removeEventListener('online', handleOnlineStatus);
            window.removeEventListener('offline', handleOnlineStatus);
        };
    }, []);

    if (isAdmin) {
        return <AdminCommunityLoading text={text === 'Cargando...' ? 'Preparando tu espacio…' : text} fullScreen={fullScreen} compact={compact} offline={!isOnline}/>;
    }

    if (!isOnline) {
        return (
            <div className={fullScreen ? "fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[99999]" : "flex items-center justify-center p-8 min-h-[50vh]"}>
                <div className="relative group">
                    <div className="absolute -inset-2 bg-gradient-to-r from-red-400 to-orange-400 rounded-2xl opacity-20 blur-xl animate-pulse"></div>
                    <div className="relative bg-white/95 dark:bg-gray-900/95 backdrop-blur-lg p-6 rounded-xl shadow-2xl flex items-center space-x-4">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-8 w-8 text-red-600"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <span className="text-gray-800 dark:text-gray-100 font-medium">Sin conexión a internet</span>
                    </div>
                </div>
            </div>
        );
    }

    const content = (
        <div className="relative flex flex-col items-center justify-center p-4 mx-auto">
            {/* Animación dinámica de Co Marca: bolitas de carga convergiendo para formar el logotipo exacto */}
            <div className="comarca-loader-box">
                {/* Logotipo exacto de Co Marca con transparencia total y curvas originales */}
                <img src={comarcaEmblem} alt="Co Marca" className="comarca-exact-emblem" />

                {/* 4 Bolitas de carga orbitando y ensamblando las cabezas del logotipo */}
                <div className="comarca-balls-orbit">
                    <div className="comarca-ball comarca-ball-tl" />
                    <div className="comarca-ball comarca-ball-tr" />
                    <div className="comarca-ball comarca-ball-bl" />
                    <div className="comarca-ball comarca-ball-br" />
                </div>
            </div>

            {/* Texto de estado con micro-puntos animados */}
            <div className="flex items-center space-x-2 mt-4">
                <span className={fullScreen ? "text-white font-semibold text-sm tracking-wide drop-shadow-sm" : "text-gray-700 dark:text-gray-200 font-semibold text-sm tracking-wide"}>
                    {text}
                </span>
                <div className="flex space-x-1">
                    {[0, 1, 2].map((i) => (
                        <div
                            key={i}
                            className="h-1.5 w-1.5 bg-[#c1348b] rounded-full animate-bounce"
                            style={{ animationDelay: `${i * 0.2}s` }}
                        />
                    ))}
                </div>
            </div>
        </div>
    );

    if (fullScreen) {
        return (
            <div data-luma-overlay="true" className="fixed inset-0 bg-black/45 dark:bg-black/65 backdrop-blur-md flex items-center justify-center z-[99999] transition-all p-4">
                {content}
            </div>
        );
    }

    return (
        <div className="cowork-loading-state w-full flex-1 flex items-center justify-center min-h-[60vh] p-6" role="status" aria-live="polite" aria-busy="true">
            {content}
        </div>
    );
};

export default Loading;
